'use client';

import { useEffect, useState } from 'react';

/**
 * useKeyboardInset
 *
 * Derives the height of the virtual keyboard (if present) from the
 * VisualViewport API. When unsupported, returns 0 so layouts behave as usual.
 *
 * Implementation notes:
 * - `window.visualViewport.height` reflects the visible portion of the page.
 * - Subtracting it from `window.innerHeight` yields the occluded area.
 * - We listen to `resize` and `scroll` on VisualViewport plus a window
 *   `resize` fallback to cover various browsers.
 */
export const useKeyboardInset = (): number => {
  const [keyboardInset, setKeyboardInset] = useState<number>(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    const recompute = () => {
      const orientation = window.screen.orientation?.type || '';
      const isLandscape = orientation.includes('landscape');
      const inset = isLandscape
        ? Math.max(0, window.innerWidth - visualViewport.width - visualViewport.offsetLeft)
        : Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop);
      setKeyboardInset(inset);
    };

    recompute();
    visualViewport.addEventListener('resize', recompute);
    visualViewport.addEventListener('scroll', recompute);

    // Some UAs fire only window resize
    window.addEventListener('resize', recompute);

    return () => {
      visualViewport.removeEventListener('resize', recompute);
      visualViewport.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, []);

  return keyboardInset;
};
